import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SearchResult {
  ticker: string;
  exchange: string;
  company_name: string;
  searched_at?: string;
}

interface AlertMetadata {
  action_label?: string;
  confidence?: number;
  final_score?: number;
  rules_score?: number;
  price_direction?: string;
  timeframe?: string;
  triggered_signals?: string[];
  key_risks?: string[];
  news_url?: string;
  news_headline?: string;
  flags?: string;
  score?: number;
}

interface Alert {
  id: number;
  ticker: string;
  exchange: string;
  alert_type: string;
  severity: string;
  action?: string;
  headline: string;
  reasoning: string;
  metadata?: AlertMetadata;
  is_read: boolean;
  created_at: string;
  notification_key?: string | null;
}


interface Ticker {
  id: number;
  ticker: string;
  exchange: string;
  specific_exchange?: string | null;
  tier: string;
  is_active: boolean;
  market_cap: number | null;
  signal_score: number | null;
  last_signal_at: string | null;
  sector: string | null;
  company_name: string | null;
  tier_reason?: string | null;
  manual_override?: boolean;
  manual_override_until?: string | null;
  manual_override_reason?: string | null;
}

interface ScanFlag {
  ticker: string;
  exchange: string;
  specific_exchange?: string;
  flag_count: number;
  latest_score: number;
  latest_reason: string;
  latest_scanned_at: string;
}

interface StockDetail {
  ticker: string;
  exchange: string;
  specific_exchange?: string;
  company_name: string;
  price?: {
    price?: number;
    change_pct?: number;
    volume?: number;
    high?: number;
    low?: number;
    open?: number;
    currency?: string;
  };
  chart_7d?: ChartBar[];
  chart_30d?: ChartBar[];
  chart_90d?: ChartBar[];
  chart_1y?: ChartBar[];
  news?: NewsItem[];
  fundamentals?: Fundamentals;
  signals?: Signal[];
  signal_score?: number | null;
  scanner_score?: number | null;
}

interface ChartBar { date: string; open: number; high: number; low: number; close: number; volume: number; }
interface NewsItem { headline: string; source: string; url: string; published_at: string; sentiment_score?: number; }
interface Fundamentals {
  pe_ratio?: number; pb_ratio?: number; market_cap?: number; revenue?: number;
  debt_to_equity?: number; roe?: number; earnings_growth?: number; dividend_yield?: number;
  fifty_two_week_high?: number; fifty_two_week_low?: number; sector?: string;
}
interface Signal {
  action: string; confidence: number; final_score: number; rationale: string;
  created_at: string; ai_action?: string; ai_confidence?: number;
  ai_reasoning?: string; ai_risks?: string; ai_news_sentiment?: string;
  breakdown?: Record<string, any>;
  valuation_score?: number; quality_score?: number; momentum_score?: number;
  sentiment_score?: number; macro_score?: number; ml_signal?: string; ml_confidence?: number;
  rules_score?: number;
}
interface QAEntry { question: string; answer: string; loading: boolean; }

// ─── API helpers ──────────────────────────────────────────────────────────────

const tk = () => sessionStorage.getItem('token') || '';

function apiGet(path: string) {
  return fetch(`/api${path}`, { headers: { Authorization: `Bearer ${tk()}` } });
}

function apiPost(path: string, body: unknown) {
  return fetch(`/api${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tk()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(path, {
    ...opts,
    headers: { Authorization: `Bearer ${tk()}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function fmtMarketCap(mc?: number | null): string {
  if (!mc) return '—';
  if (mc >= 1e12) return `$${(mc / 1e12).toFixed(1)}T`;
  if (mc >= 1e9) return `$${(mc / 1e9).toFixed(1)}B`;
  return `$${(mc / 1e6).toFixed(0)}M`;
}

function PriceChange({ pct }: { pct?: number }) {
  if (pct === undefined || pct === null) return <span className="text-text-secondary">—</span>;
  return <span className={pct >= 0 ? 'text-status-green' : 'text-status-red'}>{pct >= 0 ? '+' : ''}{pct.toFixed(2)}%</span>;
}

function formatAEST(isoString: string): string {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }) + ' AEST';
  } catch { return isoString; }
}

// ─── FIX 9: Alert helpers — plain English ────────────────────────────────────

function parseFlags(flagStr: string): string[] {
  return (flagStr || '').split(';').map(s => s.trim()).filter(Boolean);
}

function flagToPlainEnglish(flag: string): { text: string; icon: string } | null {
  flag = flag.trim();
  const priceMoveUp = flag.match(/price_move_UP_([\d.]+)pct/);
  const priceMoveDown = flag.match(/price_move_DOWN_([\d.]+)pct/);
  if (priceMoveUp) return { icon: '📈', text: `Price has moved up ${priceMoveUp[1]}% — strong upward momentum` };
  if (priceMoveDown) return { icon: '📉', text: `Price has moved down ${priceMoveDown[1]}% — significant decline` };
  if (flag === 'rsi_overbought_100' || flag.includes('rsi_overbought')) {
    const val = flag.match(/rsi_overbought_(\d+)/)?.[1];
    return { icon: '⚡', text: `RSI at ${val || 'extreme high'} — extremely overbought, consider waiting for pullback before entry` };
  }
  if (flag === 'rsi_oversold' || flag.includes('rsi_oversold')) {
    const val = flag.match(/rsi_oversold_(\d+)/)?.[1];
    return { icon: '⚡', text: `RSI at ${val || 'extreme low'} — oversold, potential bounce opportunity` };
  }
  if (flag === 'near_52w_high') return { icon: '📊', text: 'Trading near its 52-week high — strong uptrend confirmed' };
  if (flag === 'near_52w_low') return { icon: '📊', text: 'Trading near its 52-week low — potential value opportunity or downtrend' };
  if (flag.includes('volume_surge')) return { icon: '📊', text: 'Unusual volume spike — institutional activity detected' };
  if (flag.includes('golden_cross')) return { icon: '📈', text: 'Golden Cross: 50-day MA crossed above 200-day MA — confirmed uptrend signal' };
  if (flag.includes('death_cross')) return { icon: '📉', text: 'Death Cross: 50-day MA crossed below 200-day MA — confirmed downtrend signal' };
  if (flag.includes('bollinger_upper')) return { icon: '📊', text: 'Breaking above upper Bollinger Band — strong momentum continuation' };
  if (flag.includes('bollinger_lower')) return { icon: '📊', text: 'Breaking below lower Bollinger Band — potential reversal zone' };
  if (flag.includes('macd_bullish')) return { icon: '📈', text: 'MACD bullish crossover — positive momentum shift' };
  if (flag.includes('macd_bearish')) return { icon: '📉', text: 'MACD bearish crossover — negative momentum shift' };
  if (flag.includes('earnings')) return { icon: '📅', text: 'Earnings event detected — elevated volatility expected' };
  if (flag.includes('news') || flag.includes('sentiment')) return { icon: '📰', text: 'Significant news sentiment detected' };
  // Unknown flag — clean it up
  return { icon: '🔔', text: flag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) };
}

function getActionBadge(score: number): { label: string; cls: string } {
  if (score >= 6.5) return { label: 'BUY', cls: 'bg-status-green-btn text-text-bright' };
  if (score <= 3.5) return { label: 'SELL', cls: 'bg-status-red-btn text-text-bright' };
  return { label: 'WATCH', cls: 'bg-status-amber-btn text-bg-deep' };
}

// Some alerts arrive with metadata: null (score never made it into the structured
// field), but the score is still printed in the headline/reasoning text, e.g.
// "EGA Solid BUY — score 6.6/10". Used as a display fallback below.
function extractScoreFromText(text?: string | null): number | null {
  if (!text) return null;
  const m = text.match(/score\s+([0-9]+(?:\.[0-9]+)?)\s*\/\s*10/i);
  return m ? parseFloat(m[1]) : null;
}

function AlertCard({
  alert, onRead, onDetail, onViewStock,
}: {
  alert: Alert;
  onRead: (id: number) => void;
  onDetail: (a: Alert) => void;
  onViewStock: (ticker: string, exchange: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const meta = alert.metadata;
  // Use action field directly if available, else derive from score
  const actionLabel = (alert.action || '').toUpperCase();
  const resolvedScore = meta?.final_score ?? meta?.rules_score ?? meta?.confidence
    ?? extractScoreFromText(alert.headline) ?? extractScoreFromText(alert.reasoning);
  const score = resolvedScore ?? 5;
  const badge: { label: string; cls: string } = actionLabel === 'BUY'
    ? { label: 'BUY', cls: 'bg-status-green-btn text-text-bright' }
    : actionLabel === 'SELL'
    ? { label: 'SELL', cls: 'bg-status-red-btn text-text-bright' }
    : actionLabel === 'WATCH'
    ? { label: 'WATCH', cls: 'bg-status-amber-btn text-bg-deep' }
    : getActionBadge(score);

  // Parse flags for plain English — try metadata.flags first, fall back to reasoning
  const rawFlags = meta?.flags || alert.reasoning || '';
  const flags = parseFlags(rawFlags);
  const parsedFlags = flags.map(flagToPlainEnglish).filter(Boolean) as { text: string; icon: string }[];

  const leftBorderCls = badge.label === 'BUY' ? 'border-l-4 border-border-green'
    : badge.label === 'SELL' ? 'border-l-4 border-border-red'
    : 'border-l-4 border-border-amber';

  const cardBgCls = badge.label === 'BUY' ? 'border-border-green bg-status-green-soft'
    : badge.label === 'SELL' ? 'border-border-red bg-status-red-soft'
    : 'border-border-amber bg-status-amber-soft';

  return (
    <div className={`border-2 rounded-lg p-4 transition-opacity ${leftBorderCls} ${cardBgCls} ${alert.is_read ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
          <span className={`text-xs font-bold px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
          <button onClick={() => onViewStock(alert.ticker, alert.exchange)} className="font-bold text-base hover:text-status-green transition-colors">
            {alert.ticker}
          </button>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${alert.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'}`}>
            {alert.exchange}
          </span>
          {alert.notification_key && (
            <span className="text-sm" title="Notification triggered">🔔</span>
          )}
          {resolvedScore != null && (
            <span className="text-xs text-text-primary bg-surface-2 px-1.5 py-0.5 rounded">{resolvedScore.toFixed(1)}/10</span>
          )}
          {!alert.is_read && <span className="w-2 h-2 bg-text-bright rounded-full inline-block" title="Unread" />}
        </div>
        <span className="text-xs text-text-muted shrink-0 ml-2">{new Date(alert.created_at).toLocaleTimeString()}</span>
      </div>

      {/* Headline — one line when collapsed, full when expanded */}
      {alert.headline && (
        <p className={`mt-2 text-sm font-semibold leading-relaxed ${expanded ? '' : 'truncate'}`}>{alert.headline}</p>
      )}

      {expanded && (
        <>
          <p className="mt-1 text-xs text-text-muted">{alert.alert_type} · {alert.severity}</p>

          {/* Plain English flags */}
          {parsedFlags.length > 0 && (
            <ul className="mt-2 space-y-1">
              {parsedFlags.map((f, i) => (
                <li key={i} className="text-sm text-text-primary">
                  {f.icon} {f.text}
                </li>
              ))}
            </ul>
          )}

          {/* News article link */}
          {meta?.news_url && (
            <a href={meta.news_url} target="_blank" rel="noopener noreferrer"
              className="mt-2 text-xs text-status-blue hover:text-status-blue-muted underline block truncate">
              {meta.news_headline || meta.news_url}
            </a>
          )}
        </>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button onClick={() => { setExpanded(e => !e); onDetail(alert); }} className="text-xs text-text-primary hover:text-text-bright underline">
          {expanded ? 'Collapse ▲' : 'Expand ▼'}
        </button>
        <button onClick={() => onViewStock(alert.ticker, alert.exchange)}
          className="text-xs bg-status-blue-btn-hover hover:bg-status-blue-btn-hover text-text-bright px-2.5 py-1 rounded">Research →</button>
        {!alert.is_read && (
          <button onClick={() => onRead(alert.id)} className="text-xs text-text-secondary hover:text-text-bright underline ml-auto">Mark read</button>
        )}
      </div>
    </div>
  );
}

// ─── Chart ────────────────────────────────────────────────────────────────────

function SimpleChart({ bars }: { bars: ChartBar[] }) {
  if (!bars || bars.length === 0) return <div className="h-32 flex items-center justify-center text-text-muted text-sm">No chart data</div>;
  const prices = bars.map(b => b.close);
  const min = Math.min(...prices), max = Math.max(...prices), range = max - min || 1;
  const w = 600, h = 100, isUp = prices[prices.length - 1] >= prices[0];
  const points = bars.map((b, i) => `${(i / (bars.length - 1)) * w},${h - ((b.close - min) / range) * h}`).join(' ');
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
        <polyline points={points} fill="none" stroke={isUp ? '#10b981' : '#ef4444'} strokeWidth="2" />
      </svg>
      <div className="flex justify-between text-xs text-text-muted mt-1">
        <span>{bars[0]?.date}</span><span>{bars[bars.length - 1]?.date}</span>
      </div>
    </div>
  );
}

// ─── Signal card — FIX 6: signal breakdown in Research too ───────────────────

function SentimentBadge({ sentiment }: { sentiment?: string }) {
  if (!sentiment) return null;
  const colors: Record<string, string> = {
    POSITIVE: 'bg-status-green-soft text-status-green-muted', NEGATIVE: 'bg-status-red-soft text-status-red-muted',
    NEUTRAL: 'bg-surface-2 text-text-primary', MIXED: 'bg-status-amber-soft text-status-amber-muted',
  };
  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${colors[sentiment] || colors.NEUTRAL}`}>{sentiment}</span>;
}

function SignalCard({ sig }: { sig: Signal }) {
  const [expanded, setExpanded] = useState(false);
  const hasAI = !!sig.ai_action;
  // Fix 2B: hasBreakdown includes all score fields
  const hasBreakdown = !!(
    sig.breakdown ||
    sig.valuation_score != null ||
    sig.quality_score != null ||
    sig.momentum_score != null ||
    sig.sentiment_score != null ||
    sig.macro_score != null ||
    sig.ml_signal
  );
  const actionColor = sig.action === 'BUY' ? 'text-status-green' : sig.action === 'SELL' ? 'text-status-red' : 'text-status-amber';
  const actionBg = sig.action === 'BUY' ? 'bg-status-green-soft border-border-green' : sig.action === 'SELL' ? 'bg-status-red-soft border-border-red' : 'bg-status-amber-soft border-border-amber';

  const renderBreakdown = () => {
    // Fix 2B: never show "not available" if any score is present
    if (!hasBreakdown) return <p className="text-text-muted text-xs italic">Detailed breakdown not available for this signal</p>;
    const score = sig.final_score ?? sig.confidence ?? 0;
    const rows: string[] = [];
    const bd = sig.breakdown || {};

    if (sig.valuation_score != null) {
      // Use val_detail string from JSONB if available, else fall back to numeric fields
      const detail = bd.val_detail as string | undefined;
      if (detail) {
        rows.push(`Valuation (${sig.valuation_score.toFixed(1)}/10): ${detail}`);
      } else {
        const pe = bd.pe_ratio;
        const pb = bd.pb_ratio;
        let t = '';
        if (pe) t += ` P/E: ${parseFloat(pe).toFixed(1)}.`;
        if (pb) t += ` P/B: ${parseFloat(pb).toFixed(1)}.`;
        rows.push(`Valuation (${sig.valuation_score.toFixed(1)}/10):${t || ' No specific data.'}`);
      }
    }
    if (sig.quality_score != null) {
      const detail = bd.qual_detail as string | undefined;
      if (detail) {
        rows.push(`Quality (${sig.quality_score.toFixed(1)}/10): ${detail}`);
      } else {
        const roe = bd.roe;
        const de = bd.debt_to_equity;
        let t = '';
        if (roe) t += ` ROE: ${parseFloat(roe).toFixed(1)}%.`;
        if (de) t += ` D/E: ${parseFloat(de).toFixed(2)}.`;
        rows.push(`Quality (${sig.quality_score.toFixed(1)}/10):${t || ' No specific data.'}`);
      }
    }
    if (sig.momentum_score != null) {
      const detail = bd.mom_detail as string | undefined;
      if (detail) {
        rows.push(`Momentum (${sig.momentum_score.toFixed(1)}/10): ${detail}`);
      } else {
        const r1 = bd.return_1d, r5 = bd.return_5d, r20 = bd.return_20d;
        let t = '';
        if (r1 != null) t += ` 1d: ${(parseFloat(r1)*100).toFixed(1)}%.`;
        if (r5 != null) t += ` 5d: ${(parseFloat(r5)*100).toFixed(1)}%.`;
        if (r20 != null) t += ` 20d: ${(parseFloat(r20)*100).toFixed(1)}%.`;
        rows.push(`Momentum (${sig.momentum_score.toFixed(1)}/10):${t || ' Insufficient price history.'}`);
      }
    }
    if (sig.sentiment_score != null) {
      const detail = bd.sent_detail as string | undefined;
      if (detail) {
        rows.push(`Sentiment (${sig.sentiment_score.toFixed(1)}/10): ${detail}`);
      } else {
        const t = sig.sentiment_score >= 7 ? 'Very positive news sentiment.' : sig.sentiment_score >= 5 ? 'Neutral to mixed sentiment.' : 'Negative news sentiment.';
        rows.push(`Sentiment (${sig.sentiment_score.toFixed(1)}/10): ${t}`);
      }
    }
    if (sig.macro_score != null) {
      const detail = bd.mac_detail as string | undefined;
      if (detail) {
        rows.push(`Macro (${sig.macro_score.toFixed(1)}/10): ${detail}`);
      } else {
        const t = sig.macro_score >= 7 ? 'Favourable macro conditions.' : sig.macro_score >= 5 ? 'Neutral macro environment.' : 'Challenging macro conditions.';
        rows.push(`Macro (${sig.macro_score.toFixed(1)}/10): ${t}`);
      }
    }
    if (sig.ml_signal) {
      rows.push(`ML Model: XGBoost signal: ${sig.ml_signal}${sig.ml_confidence != null ? `, confidence ${(sig.ml_confidence * 100).toFixed(0)}%` : ''}`);
    }
    if (sig.rules_score != null) {
      rows.push(`Final: Rules 60% + ML 40% = ${score.toFixed(1)}/10`);
    }

    return (
      <div className="mt-2 pt-2 border-t border-border-base">
        <p className="text-xs font-semibold mb-1">
          Score: {score.toFixed(1)}/10 — <span className={actionColor}>{sig.action}</span> signal
        </p>
        {rows.map((r, i) => <p key={i} className="text-xs text-text-secondary">• {r}</p>)}
        {rows.length === 0 && <p className="text-xs text-text-muted italic">No detailed breakdown data.</p>}
      </div>
    );
  };

  return (
    <div className={`border rounded-lg p-3 text-sm ${actionBg}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
          <span className={`font-bold text-base ${actionColor}`}>{sig.action}</span>
          <span className="text-text-primary text-xs font-medium bg-surface-2 px-2 py-0.5 rounded-full">{sig.confidence?.toFixed(1)}/10</span>
          {hasAI && <span className="text-xs bg-accent-soft text-text-secondary px-1.5 py-0.5 rounded">AI: {sig.ai_action}</span>}
          {hasAI && <SentimentBadge sentiment={sig.ai_news_sentiment} />}
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-text-secondary hover:text-text-bright ml-2 shrink-0">
          {expanded ? 'Less ▲' : 'Details ▼'}
        </button>
      </div>
      {sig.created_at && <p className="text-text-muted text-xs mt-1">{formatAEST(sig.created_at)}</p>}
      <p className="text-text-secondary text-xs mt-1">{sig.rationale}</p>
      {expanded && (
        <div className="mt-3 space-y-2 border-t border-border-base pt-3">
          {renderBreakdown()}
          {hasAI && (
            <div className="bg-accent-soft border border-border-light rounded p-2">
              <p className="text-text-secondary text-xs font-medium mb-1">Claude AI Analysis (confidence: {sig.ai_confidence?.toFixed(1)}/10)</p>
              <p className="text-text-primary text-xs">{sig.ai_reasoning}</p>
            </div>
          )}
          {hasAI && sig.ai_risks && (
            <div className="bg-status-red-soft border border-border-red rounded p-2">
              <p className="text-status-red-muted text-xs font-medium mb-1">Risks</p>
              <p className="text-text-primary text-xs">{sig.ai_risks}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Fundamental guide ────────────────────────────────────────────────────────

interface FundGuide { label: string; explanation: string; healthy: string; ranges: { label: string; range: string; color: string }[]; }
const FUND_GUIDES: Record<string, FundGuide> = {
  pe_ratio: {
    label: 'Price-to-Earnings (P/E) Ratio',
    explanation: 'Compares the stock price to annual earnings per share.',
    healthy: 'For most established sectors, 15–25 is fair value.',
    ranges: [{ label: 'Undervalued', range: '< 10', color: 'text-status-green' }, { label: 'Fair Value', range: '10–25', color: 'text-status-blue' }, { label: 'Expensive', range: '25–40', color: 'text-status-amber' }, { label: 'Speculative', range: '> 40', color: 'text-status-red' }],
  },
  pb_ratio: {
    label: 'Price-to-Book (P/B) Ratio',
    explanation: "Compares a company's market value to its book value.",
    healthy: 'Below 3 is generally fair. Under 1 can be a bargain.',
    ranges: [{ label: 'Deep Value', range: '< 1', color: 'text-status-green' }, { label: 'Fair Value', range: '1–3', color: 'text-status-blue' }, { label: 'Expensive', range: '3–6', color: 'text-status-amber' }, { label: 'Speculative', range: '> 6', color: 'text-status-red' }],
  },
  debt_to_equity: {
    label: 'Debt-to-Equity (D/E) Ratio',
    explanation: 'Measures how much debt a company carries relative to shareholder equity.',
    healthy: 'Under 1.0 is conservative. 1–2 is normal for capital-intensive industries.',
    ranges: [{ label: 'Conservative', range: '< 0.5', color: 'text-status-green' }, { label: 'Moderate', range: '0.5–2', color: 'text-status-blue' }, { label: 'Elevated', range: '2–3', color: 'text-status-amber' }, { label: 'High Risk', range: '> 3', color: 'text-status-red' }],
  },
  roe: {
    label: 'Return on Equity (ROE)',
    explanation: "Measures how much profit the company generates for every dollar of shareholders' equity.",
    healthy: 'Above 15% is strong.',
    ranges: [{ label: 'Weak', range: '< 5%', color: 'text-status-red' }, { label: 'Average', range: '5–15%', color: 'text-status-amber' }, { label: 'Strong', range: '15–25%', color: 'text-status-blue' }, { label: 'Exceptional', range: '> 25%', color: 'text-status-green' }],
  },
  earnings_growth: {
    label: 'Earnings Growth (YoY)',
    explanation: 'Year-over-year percentage change in earnings per share.',
    healthy: 'Double-digit growth (10%+) is solid.',
    ranges: [{ label: 'Declining', range: '< 0%', color: 'text-status-red' }, { label: 'Slow', range: '0–10%', color: 'text-status-amber' }, { label: 'Healthy', range: '10–20%', color: 'text-status-blue' }, { label: 'High Growth', range: '> 20%', color: 'text-status-green' }],
  },
  market_cap: {
    label: 'Market Capitalisation',
    explanation: 'Total market value of all outstanding shares.',
    healthy: 'Large caps ($10B+) suit conservative investors; small caps (<$2B) offer higher potential with higher volatility.',
    ranges: [{ label: 'Small Cap', range: '< $2B', color: 'text-status-amber' }, { label: 'Mid Cap', range: '$2B–$10B', color: 'text-status-blue' }, { label: 'Large Cap', range: '$10B–$100B', color: 'text-status-green' }, { label: 'Mega Cap', range: '> $100B', color: 'text-text-secondary' }],
  },
};

function FundGuidePanel({ guide }: { guide: FundGuide }) {
  return (
    <div className="mt-2 mb-1 bg-surface-2 border border-border-light rounded-lg p-4 text-xs space-y-3">
      <div>
        <p className="font-semibold text-text-bright text-sm mb-1">{guide.label}</p>
        <p className="text-text-primary leading-relaxed">{guide.explanation}</p>
      </div>
      <p className="text-text-primary">{guide.healthy}</p>
      <div className="grid grid-cols-2 gap-1.5">
        {guide.ranges.map((r, i) => (
          <div key={i} className="flex items-center justify-between bg-surface rounded px-2 py-1">
            <span className={`font-medium ${r.color}`}>{r.label}</span>
            <span className="text-text-secondary ml-2">{r.range}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Stock Detail Panel ───────────────────────────────────────────────────────

const PRESET_QUESTIONS = [
  'Should I buy?', 'I own this — what should I do?', 'What are the risks?',
  'How does it compare to peers?', 'What would change the outlook?', 'Explain it simply',
];

function StockDetailPanel({ stock, onClose }: { stock: StockDetail; onClose: () => void }) {
  const [chartPeriod, setChartPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [openFundGuide, setOpenFundGuide] = useState<string | null>(null);
  const [showSignalGuide, setShowSignalGuide] = useState(false);
  const [qaEntries, setQaEntries] = useState<QAEntry[]>([]);
  const [fetchingFundamentals, setFetchingFundamentals] = useState(false);
  const [stockData, setStockData] = useState(stock);
  const answerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setStockData(stock); setQaEntries([]); setOpenFundGuide(null); }, [stock.ticker, stock.exchange]);

  const toggleFundGuide = (key: string) => setOpenFundGuide(prev => prev === key ? null : key);
  const InfoIcon = ({ metricKey }: { metricKey: string }) => (
    <button
      onClick={() => toggleFundGuide(metricKey)}
      className={`ml-1 w-4 h-4 rounded-full text-xs flex items-center justify-center transition-colors ${
        openFundGuide === metricKey ? 'bg-status-blue-btn text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-status-blue-btn-hover hover:text-text-bright'
      }`}
    >i</button>
  );

  const askQuestion = async (question: string) => {
    const newEntry: QAEntry = { question, answer: '', loading: true };
    setQaEntries(prev => [...prev, newEntry]);
    const idx = qaEntries.length;
    try {
      const res = await apiPost('/explorer/ai-question', {
        ticker: stockData.ticker, exchange: stockData.exchange, question,
        price_data: stockData.price || {}, fundamentals_data: stockData.fundamentals || {},
        signals_summary: stockData.signals?.map(s => `${s.action} ${s.confidence}/10`).join(', ') || '',
        news_headlines: stockData.news?.slice(0, 8).map(n => n.headline) || [],
      });
      if (res.ok) {
        const data = await res.json();
        setQaEntries(prev => prev.map((e, i) => i === idx ? { ...e, answer: data.answer, loading: false } : e));
      } else {
        setQaEntries(prev => prev.map((e, i) => i === idx ? { ...e, answer: 'AI analysis unavailable — please try again.', loading: false } : e));
      }
    } catch {
      setQaEntries(prev => prev.map((e, i) => i === idx ? { ...e, answer: 'Network error — please try again.', loading: false } : e));
    }
  };

  const refreshFundamentals = () => {
    setFetchingFundamentals(true);
    apiPost(`/explorer/fetch-fundamentals/${stockData.ticker}?exchange=${stockData.exchange}`, {})
      .then(r => r.ok ? r.json() : null)
      .then(fd => { if (fd?.fundamentals) setStockData(prev => ({ ...prev, fundamentals: fd.fundamentals })); })
      .catch(() => {})
      .finally(() => setFetchingFundamentals(false));
  };

  const chartData: Record<string, ChartBar[] | undefined> = {
    '7d': stockData.chart_7d, '30d': stockData.chart_30d, '90d': stockData.chart_90d, '1y': stockData.chart_1y,
  };
  const formatRevenue = (rev?: number) => {
    if (!rev) return null;
    if (rev >= 1e12) return `$${(rev / 1e12).toFixed(1)}T`;
    if (rev >= 1e9) return `$${(rev / 1e9).toFixed(1)}B`;
    return `$${(rev / 1e6).toFixed(0)}M`;
  };

  return (
    <div className="border border-border-green rounded-xl bg-surface shadow-xl overflow-hidden">
      <div className="bg-surface border-b border-border-base px-5 py-4 flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h2 className="text-3xl font-bold">{stockData.ticker}</h2>
            <span className={`text-sm px-2 py-0.5 rounded ${stockData.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'}`}>
              {stockData.specific_exchange || stockData.exchange}
            </span>
          </div>
          <p className="text-text-secondary mt-1">{stockData.company_name}</p>
          <button
            onClick={() => navigate(`/?override=${stockData.ticker}&exchange=${encodeURIComponent(stockData.specific_exchange || stockData.exchange)}`)}
            className="mt-2 text-xs bg-status-amber-btn-hover hover:bg-status-amber-btn text-text-bright px-2 py-1 rounded whitespace-nowrap"
            title="Go to Manual Override panel"
          >⚡ Manual Override</button>
        </div>
        <div className="flex items-start gap-4">
          {stockData.price && (
            <div className="text-right">
              <p className="text-3xl font-bold">{stockData.price.currency === 'AUD' ? 'A' : ''}${(stockData.price.price || 0).toFixed(2)}</p>
              <p className="text-lg"><PriceChange pct={stockData.price.change_pct} /></p>
              <p className="text-text-secondary text-xs mt-1">Vol: {(stockData.price.volume || 0).toLocaleString()}</p>
            </div>
          )}
          <button onClick={onClose} className="text-text-secondary hover:text-text-bright text-xl">✕</button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {stockData.price && (
          <div className="grid grid-cols-3 gap-4 text-sm">
            {[['Open', stockData.price.open], ['High', stockData.price.high], ['Low', stockData.price.low]].map(([l, v]) => (
              <div key={l as string} className="bg-surface rounded p-3">
                <p className="text-text-secondary text-xs">{l}</p>
                <p>${((v as number) || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
        )}

        {(chartData[chartPeriod]?.length ?? 0) > 0 && (
          <div className="bg-surface rounded-lg border border-border-base p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Price Chart</h3>
              <div className="flex space-x-1">
                {(['7d', '30d', '90d', '1y'] as const).map(p => (
                  <button key={p} onClick={() => setChartPeriod(p)} className={`text-xs px-2.5 py-1 rounded ${chartPeriod === p ? 'bg-status-green-btn text-text-bright' : 'bg-surface-2 text-text-secondary hover:bg-surface-hover'}`}>{p}</button>
                ))}
              </div>
            </div>
            <SimpleChart bars={chartData[chartPeriod] || []} />
          </div>
        )}

        {/* Score boxes — Scanner Score + System Score */}
        <div className="grid grid-cols-2 gap-4">
          {(() => {
            const scoreCls = (s: number | null | undefined) =>
              s == null ? 'text-text-secondary'
              : s >= 6.5 ? 'text-status-green'
              : s <= 3.5 ? 'text-status-red'
              : 'text-status-amber';
            const scanScore = stockData.scanner_score;
            const sysScore = stockData.signal_score;
            return (
              <>
                <div className="bg-surface rounded-lg border border-border-base p-4">
                  <p className="text-xs text-text-secondary mb-1">Scanner Score</p>
                  <p className={`text-2xl font-bold ${scoreCls(scanScore)}`}>
                    {scanScore != null ? scanScore.toFixed(1) : '—'}
                  </p>
                  <p className="text-xs text-text-muted mt-1">Rule-based technical scoring</p>
                </div>
                <div className="bg-surface rounded-lg border border-border-base p-4">
                  <p className="text-xs text-text-secondary mb-1">System Score</p>
                  <p className={`text-2xl font-bold ${scoreCls(sysScore)}`}>
                    {sysScore != null ? sysScore.toFixed(1) : '—'}
                  </p>
                  <p className="text-xs text-text-muted mt-1">Full signal engine score (Rules + ML blend)</p>
                </div>
              </>
            );
          })()}
        </div>

        <div className="bg-surface rounded-lg border border-border-base p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Fundamentals</h3>
            <button onClick={refreshFundamentals} className="text-xs text-text-secondary hover:text-text-bright underline">Refresh</button>
          </div>
          {fetchingFundamentals ? (
            <div className="flex items-center gap-2 text-text-secondary text-sm py-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Fetching...
            </div>
          ) : stockData.fundamentals && Object.keys(stockData.fundamentals).length > 0 ? (
            <div className="grid grid-cols-2 gap-x-8 text-sm">
              <div className="space-y-1">
                {stockData.fundamentals.pe_ratio != null && (<>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary flex items-center">P/E Ratio<InfoIcon metricKey="pe_ratio" /></span>
                    <span>{stockData.fundamentals.pe_ratio.toFixed(2)}</span>
                  </div>
                  {openFundGuide === 'pe_ratio' && <FundGuidePanel guide={FUND_GUIDES.pe_ratio} />}
                </>)}
                {stockData.fundamentals.pb_ratio != null && (<>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary flex items-center">P/B Ratio<InfoIcon metricKey="pb_ratio" /></span>
                    <span>{stockData.fundamentals.pb_ratio.toFixed(2)}</span>
                  </div>
                  {openFundGuide === 'pb_ratio' && <FundGuidePanel guide={FUND_GUIDES.pb_ratio} />}
                </>)}
                {stockData.fundamentals.debt_to_equity != null && (<>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary flex items-center">Debt/Equity<InfoIcon metricKey="debt_to_equity" /></span>
                    <span>{stockData.fundamentals.debt_to_equity.toFixed(2)}</span>
                  </div>
                  {openFundGuide === 'debt_to_equity' && <FundGuidePanel guide={FUND_GUIDES.debt_to_equity} />}
                </>)}
                {stockData.fundamentals.roe != null && (<>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary flex items-center">ROE<InfoIcon metricKey="roe" /></span>
                    <span>{stockData.fundamentals.roe.toFixed(1)}%</span>
                  </div>
                  {openFundGuide === 'roe' && <FundGuidePanel guide={FUND_GUIDES.roe} />}
                </>)}
                {stockData.fundamentals.earnings_growth != null && (<>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary flex items-center">Earnings Growth<InfoIcon metricKey="earnings_growth" /></span>
                    <span className={stockData.fundamentals.earnings_growth >= 0 ? 'text-status-green' : 'text-status-red'}>{stockData.fundamentals.earnings_growth.toFixed(1)}%</span>
                  </div>
                  {openFundGuide === 'earnings_growth' && <FundGuidePanel guide={FUND_GUIDES.earnings_growth} />}
                </>)}
              </div>
              <div className="space-y-1">
                {stockData.fundamentals.market_cap != null && (<>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary flex items-center">Market Cap<InfoIcon metricKey="market_cap" /></span>
                    <span>{fmtMarketCap(stockData.fundamentals.market_cap)}</span>
                  </div>
                  {openFundGuide === 'market_cap' && <FundGuidePanel guide={FUND_GUIDES.market_cap} />}
                </>)}
                {stockData.fundamentals.revenue != null && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">Revenue</span>
                    <span>{formatRevenue(stockData.fundamentals.revenue)}</span>
                  </div>
                )}
                {stockData.fundamentals.dividend_yield != null && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">Dividend Yield</span>
                    <span>{(stockData.fundamentals.dividend_yield * 100).toFixed(2)}%</span>
                  </div>
                )}
                {(stockData.fundamentals.fifty_two_week_high != null || stockData.fundamentals.fifty_two_week_low != null) && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">52-Week Range</span>
                    <span className="text-xs">
                      <span className="text-status-red">${stockData.fundamentals.fifty_two_week_low?.toFixed(2) ?? '—'}</span>
                      <span className="text-text-muted mx-1">–</span>
                      <span className="text-status-green">${stockData.fundamentals.fifty_two_week_high?.toFixed(2) ?? '—'}</span>
                    </span>
                  </div>
                )}
                {stockData.fundamentals.sector && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">Sector</span>
                    <span className="text-xs text-status-blue-muted">{stockData.fundamentals.sector}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-sm py-1">
              <p className="text-text-muted">Fundamentals not available</p>
              <button onClick={refreshFundamentals} className="text-xs bg-surface-2 hover:bg-surface-hover text-text-bright px-2.5 py-1 rounded">Fetch now</button>
            </div>
          )}
        </div>

        <div className="bg-surface rounded-lg border border-border-base p-4">
          <div className="flex items-center mb-3">
            <h3 className="font-semibold text-sm">Recent Signals</h3>
            <button
              onClick={() => setShowSignalGuide(p => !p)}
              className={`ml-2 w-4 h-4 rounded-full text-xs flex items-center justify-center transition-colors ${showSignalGuide ? 'bg-status-blue-btn text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-status-blue-btn-hover hover:text-text-bright'}`}
            >i</button>
          </div>
          {showSignalGuide && (
            <div className="mb-3 bg-surface-2 border border-border-light rounded-lg p-3 text-xs space-y-2">
              <p className="text-text-primary">Score ≥ 6.5 = BUY &nbsp;|&nbsp; 3.5–6.5 = HOLD &nbsp;|&nbsp; ≤ 3.5 = SELL. Click "Details" to expand breakdown.</p>
            </div>
          )}
          {stockData.signals && stockData.signals.length > 0 ? (
            <div className="space-y-3">
              {stockData.signals.map((sig, i) => <SignalCard key={i} sig={sig} />)}
            </div>
          ) : (
            <p className="text-text-muted text-sm">No signals yet for {stockData.ticker}</p>
          )}
        </div>

        <div className="bg-surface rounded-lg border border-border-base p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Ask AI About {stockData.ticker}</h3>
            {qaEntries.length > 0 && <button onClick={() => setQaEntries([])} className="text-xs text-text-muted hover:text-text-primary">Clear</button>}
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {PRESET_QUESTIONS.map(q => (
              <button key={q} onClick={() => askQuestion(q)} disabled={qaEntries.some(e => e.question === q && e.loading)}
                className="bg-surface-2 hover:bg-surface-hover disabled:opacity-50 border border-border-light hover:border-border-green text-sm text-text-primary px-3 py-1.5 rounded-full transition-colors"
              >{q}</button>
            ))}
          </div>
          {qaEntries.length > 0 && (
            <div ref={answerRef} className="space-y-3">
              {qaEntries.map((entry, i) => (
                <div key={i} className="border border-border-base rounded-lg p-3">
                  <p className="text-sm font-medium text-status-green mb-2">Q: {entry.question}</p>
                  {entry.loading
                    ? <div className="flex items-center gap-2 text-text-secondary text-sm"><span className="animate-spin">⟳</span><span>Analysing {stockData.ticker}...</span></div>
                    : <p className="text-sm text-text-primary leading-relaxed">{entry.answer}</p>
                  }
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-surface rounded-lg border border-border-base p-4">
          <h3 className="font-semibold text-sm mb-3">News ({stockData.news?.length || 0})</h3>
          {stockData.news && stockData.news.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {stockData.news.map((article, i) => (
                <div key={i} className="border-b border-border-base pb-3 last:border-0">
                  <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-status-green transition-colors">{article.headline}</a>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-text-muted text-xs">{article.source}</span>
                    <span className="text-text-muted text-xs">{new Date(article.published_at).toLocaleString()}</span>
                    {article.sentiment_score != null && (
                      <span className={`text-xs font-medium ${article.sentiment_score > 0.2 ? 'text-status-green' : article.sentiment_score < -0.2 ? 'text-status-red' : 'text-text-secondary'}`}>
                        {article.sentiment_score > 0 ? '▲' : '▼'} {Math.abs(article.sentiment_score).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-text-muted text-sm">No news in database yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Watch This filter pills ──────────────────────────────────────────────────

type AlertActionFilter = 'ALL' | 'BUY' | 'SELL' | 'WATCH';

// ─── Watch This Tab ───────────────────────────────────────────────────────────

const US_EXCHANGES = new Set(['NYSE', 'NASDAQ', 'AMEX', 'NYSE Arca', 'BATS']);
const LEGACY_ALERT_MODAL_ENABLED = false;

function WatchThisTab({ onViewStock }: { onViewStock: (ticker: string, exchange: string) => void }) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 100;

  const [actionFilter, setActionFilter] = useState<AlertActionFilter>('ALL');
  const [notifFilter, setNotifFilter] = useState(false);
  const [exchangeFilter, setExchangeFilter] = useState<'ALL' | 'ASX' | 'US'>('ALL');

  const load = useCallback(async (newOffset = 0, append = false, currentActionFilter?: AlertActionFilter) => {
    const af = currentActionFilter ?? actionFilter;
    if (newOffset === 0) setLoading(true);
    else setLoadingMore(true);
    try {
      const actionParam = af !== 'ALL' ? `&action=${af}` : '';
      const res = await apiGet(`/watch-this?limit=${PAGE_SIZE}&offset=${newOffset}${actionParam}`);
      if (res.ok) {
        const data = await res.json();
        const newAlerts = data.alerts || [];
        setTotalCount(data.total || 0);
        if (append) {
          setAlerts(prev => [...prev, ...newAlerts]);
        } else {
          setAlerts(newAlerts);
        }
        setOffset(newOffset + newAlerts.length);
      }
    } catch {} finally { setLoading(false); setLoadingMore(false); }
  }, [actionFilter]);

  useEffect(() => { load(0); const t = setInterval(() => load(0), 60000); return () => clearInterval(t); }, [load]);

  const handleRead = async (id: number) => {
    await apiFetch(`/api/watch-this/${id}/read`, { method: 'POST' });
    setAlerts(p => p.map(a => a.id === id ? { ...a, is_read: true } : a));
  };

  const handleDetail = (alert: Alert) => { setSelectedAlert(alert); if (!alert.is_read) handleRead(alert.id); };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  const toggleActionFilter = (f: AlertActionFilter) => {
    const next = f === actionFilter ? 'ALL' : f;
    setActionFilter(next);
    if (next === 'ALL') setNotifFilter(false);
    setAlerts([]);
    setOffset(0);
    load(0, false, next);
  };

  const filteredAlerts = [...alerts]
    .filter(a => {
      if (exchangeFilter === 'ASX') return a.exchange === 'ASX';
      if (exchangeFilter === 'US') return US_EXCHANGES.has(a.exchange) || a.exchange === 'US';
      return true;
    })
    .sort((a, b) => ((b as any).final_score ?? -1) - ((a as any).final_score ?? -1));
  const displayedAlerts = notifFilter ? filteredAlerts.filter(a => a.notification_key) : filteredAlerts;

  const actionPills: { key: AlertActionFilter; label: string; cls: string }[] = [
    { key: 'ALL', label: 'All', cls: 'bg-surface-2' },
    { key: 'BUY', label: 'BUY', cls: 'bg-status-green-btn' },
    { key: 'SELL', label: 'SELL', cls: 'bg-status-red-btn' },
    { key: 'WATCH', label: 'WATCH', cls: 'bg-status-amber-btn' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {actionPills.map(({ key, label, cls }) => (
          <button
            key={key}
            onClick={() => {
              const next = key === actionFilter ? 'ALL' : key;
              setActionFilter(next);
              if (next === 'ALL') setNotifFilter(false);
              setAlerts([]);
              setOffset(0);
              load(0, false, next);
            }}
            className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
              actionFilter === key ? `${cls} text-text-bright` : 'bg-surface-2 text-text-secondary hover:bg-surface-hover'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setNotifFilter(f => !f)}
          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${notifFilter ? 'bg-status-blue-btn text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-surface-hover'}`}
        >
          🔔 Alerts
        </button>
        <span className="ml-auto flex items-center gap-3">
          <span className="text-sm text-text-secondary">{totalCount} total (30 days)</span>
          {unreadCount > 0 && <span className="text-xs bg-status-red-btn text-text-bright px-2 py-0.5 rounded-full">{unreadCount} new</span>}
          <button onClick={() => load(0)} className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-3 py-1 rounded">Refresh</button>
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {(['ALL', 'ASX', 'US'] as const).map(ex => (
          <button key={ex} onClick={() => setExchangeFilter(ex)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${exchangeFilter === ex ? 'bg-status-blue-btn border-border-blue text-text-bright' : 'border-border-light text-text-secondary hover:border-border-light'}`}>
            {ex === 'ALL' ? 'All Markets' : ex}
          </button>
        ))}
      </div>

      {loading && <p className="text-text-secondary text-sm">Loading alerts...</p>}

      <div className="max-h-[70vh] overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3">
          {displayedAlerts.map(a => <AlertCard key={a.id} alert={a} onRead={handleRead} onDetail={handleDetail} onViewStock={onViewStock} />)}
        </div>
        {displayedAlerts.length === 0 && !loading && (
          <div className="text-center py-10 text-text-muted">
            <p className="text-3xl mb-2">✓</p>
            <p className="text-sm">No alerts match the current filters</p>
          </div>
        )}
        {alerts.length < totalCount && !loading && (
          <div className="text-center pt-3">
            <button
              onClick={() => load(offset, true)}
              disabled={loadingMore}
              className="bg-surface-2 hover:bg-surface-hover disabled:opacity-50 text-text-bright text-sm px-6 py-2 rounded"
            >
              {loadingMore ? 'Loading...' : `Load More (${totalCount - alerts.length} remaining)`}
            </button>
          </div>
        )}
      </div>

      {/* Superseded by inline tile expand/collapse — kept rather than deleted, not reachable from the UI */}
      {selectedAlert && LEGACY_ALERT_MODAL_ENABLED && (
        <div className="fixed inset-0 bg-surface-60 z-50 flex items-center justify-center p-4">
          <div className="border rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto bg-surface border-border-base">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center flex-wrap gap-2 mb-1">
                  <span className="font-bold text-xl">{selectedAlert.ticker}</span>
                  <span className="text-xs px-2 py-0.5 rounded font-bold bg-surface-2 text-text-bright"> {selectedAlert.severity}</span>
                </div>
                <p className="text-text-secondary text-sm">{selectedAlert.alert_type} · {selectedAlert.exchange}</p>
              </div>
              <button onClick={() => setSelectedAlert(null)} className="text-text-secondary hover:text-text-bright text-xl ml-4">✕</button>
            </div>
            {selectedAlert.headline && !selectedAlert.headline.includes('flagged by scanner') && (<h3 className='font-semibold text-lg mb-4'>{selectedAlert.headline}</h3>)}
            {selectedAlert.reasoning && (
              <div className="bg-surface-2 rounded p-3 text-sm text-text-primary mb-4">
                <p className="text-text-secondary text-xs mb-1">📊 Analysis</p>
                {(() => {
                  const rawFlags = selectedAlert.metadata?.flags || selectedAlert.reasoning || '';
                  const flags = parseFlags(rawFlags);
                  const parsed = flags.map(flagToPlainEnglish).filter(Boolean) as { text: string; icon: string }[];
                  if (parsed.length > 0) {
                    return (
                      <ul className="space-y-1">
                        {parsed.map((p, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span>{p.icon}</span>
                            <span>{p.text}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setSelectedAlert(null); onViewStock(selectedAlert.ticker, selectedAlert.exchange); }}
                className="flex-1 bg-status-blue-btn-hover hover:bg-status-blue-btn-hover text-text-bright text-sm py-2 rounded">
                Research {selectedAlert.ticker} →
              </button>
            </div>
            <p className="text-text-muted text-xs mt-4">{new Date(selectedAlert.created_at).toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FIX 8: Watchlist Tab with manual tier modal ──────────────────────────────

const DURATION_OPTIONS = [
  { label: '1 Week', days: 7 },
  { label: '2 Weeks', days: 14 },
  { label: '1 Month', days: 30 },
  { label: 'Permanent', days: null },
];

function ManualOverrideModal({
  ticker, action, onConfirm, onClose,
}: {
  ticker: Ticker;
  action: 'promote' | 'demote';
  onConfirm: (duration: number | null, reason: string) => void;
  onClose: () => void;
}) {
  const [selectedDuration, setSelectedDuration] = useState<number | null>(7);
  const [reason, setReason] = useState('');

  const untilDate = selectedDuration
    ? new Date(Date.now() + selectedDuration * 86400000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="fixed inset-0 bg-surface-60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border-base rounded-xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">
            {action === 'promote' ? 'Promote to Active' : 'Demote to Watchlist'}: {ticker.ticker}
          </h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-bright text-xl">×</button>
        </div>

        <div>
          <label className="text-text-secondary text-xs mb-2 block">How long should this manual assignment last?</label>
          <div className="grid grid-cols-2 gap-2">
            {DURATION_OPTIONS.map(opt => (
              <button
                key={opt.label}
                onClick={() => setSelectedDuration(opt.days)}
                className={`py-2 px-3 rounded border text-sm transition-colors ${
                  selectedDuration === opt.days
                    ? 'bg-status-green-btn-hover border-border-green text-text-bright'
                    : 'bg-surface-2 border-border-light text-text-primary hover:border-border-light'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {untilDate && (
            <p className="text-xs text-text-secondary mt-2">
              Bot resumes automatic management on {untilDate}
            </p>
          )}
          {!untilDate && (
            <p className="text-xs text-text-secondary mt-2">Permanent manual assignment — bot will never auto-reassign this ticker</p>
          )}
        </div>

        <div>
          <label className="text-text-secondary text-xs mb-1 block">Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Earnings coming up, hold for now"
            className="w-full bg-surface-2 border border-border-light rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={() => onConfirm(selectedDuration, reason)}
            className={`flex-1 py-2 rounded text-sm font-medium ${
              action === 'promote' ? 'bg-status-green-btn hover:bg-status-green-btn-hover' : 'bg-status-amber-btn hover:bg-status-amber-btn-hover'
            } text-text-bright`}
          >
            Confirm {action === 'promote' ? 'Promote' : 'Demote'}
          </button>
          <button onClick={onClose} className="flex-1 bg-surface-2 hover:bg-surface-hover text-text-bright py-2 rounded text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Load All — shared "fetch full set as a static snapshot" hook ────────────
// Used by both Watch List and Scanner Flags (Session 5b Phases 4/5) — one
// implementation, reused rather than rebuilt per tab.

interface LoadAllState<T> {
  active: boolean;
  items: T[];
  loadedAt: Date | null;
  loading: boolean;
}

function useLoadAll<T>(fetchAll: () => Promise<T[]>) {
  const [state, setState] = useState<LoadAllState<T>>({ active: false, items: [], loadedAt: null, loading: false });

  const trigger = useCallback(async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const items = await fetchAll();
      setState({ active: true, items, loadedAt: new Date(), loading: false });
    } catch {
      setState(s => ({ ...s, loading: false }));
    }
  }, [fetchAll]);

  const refresh = useCallback(async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const items = await fetchAll();
      setState(s => ({ ...s, items, loadedAt: new Date(), loading: false }));
    } catch {
      setState(s => ({ ...s, loading: false }));
    }
  }, [fetchAll]);

  const reset = useCallback(() => {
    setState({ active: false, items: [], loadedAt: null, loading: false });
  }, []);

  return { ...state, trigger, refresh, reset };
}

function LoadAllBanner({ loadedAt, loading, onRefresh, onExit }: {
  loadedAt: Date | null; loading: boolean; onRefresh: () => void; onExit: () => void;
}) {
  return (
    <div className="bg-status-amber-soft border border-border-amber text-status-amber-muted px-4 py-2 rounded text-sm flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span>⚠️ This data is static and will not auto-update — refresh manually for current values.</span>
        {loadedAt && (
          <span className="text-xs text-text-muted">
            Loaded at {loadedAt.toLocaleTimeString('en-AU', { hour12: false })}
          </span>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={onRefresh} disabled={loading} className="text-xs bg-surface-2 hover:bg-surface-hover disabled:opacity-50 text-text-bright px-3 py-1 rounded">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
        <button onClick={onExit} className="text-xs text-text-secondary hover:text-text-primary underline">Exit Load All</button>
      </div>
    </div>
  );
}

// Scrollable window capping DOM row count — real limit, not just a CSS clip,
// since Load All can hold thousands of rows. `children` is the caller's own
// <table>; this only supplies the scroll box and the optional reveal control.
export function ScrollWindow({ children, showRevealButton, revealLabel, onReveal, revealing }: {
  children: React.ReactNode;
  showRevealButton: boolean;
  revealLabel?: string;
  onReveal?: () => void;
  revealing?: boolean;
}) {
  // ~20 rows visible before scrolling: sticky thead (~33px, text-xs/16px line-height +
  // py-2/16px padding + border) + 20 data rows (~37px each, text-sm/20px line-height +
  // py-2/16px padding + border) = 33 + 20*37 = 773px.
  return (
    <div className="max-h-[773px] overflow-y-auto">
      {children}
      {showRevealButton && (
        <div className="text-center py-3 border-t border-border-base bg-surface sticky bottom-0">
          <button
            onClick={onReveal}
            disabled={revealing}
            className="bg-surface-2 hover:bg-surface-hover disabled:opacity-50 text-text-bright text-sm px-6 py-2 rounded"
          >
            {revealing ? 'Loading...' : revealLabel}
          </button>
        </div>
      )}
    </div>
  );
}

const US_SPECIFIC_EXCHANGES = new Set(['NYSE', 'NASDAQ', 'AMEX', 'NYSE ARCA', 'CHI-X']);

function tickerMatchesExchangeFilter(t: Ticker, exchangeFilter: string): boolean {
  if (exchangeFilter === 'ALL') return true;
  const up = exchangeFilter.toUpperCase();
  if (US_SPECIFIC_EXCHANGES.has(up)) return (t.specific_exchange || '').toUpperCase() === up;
  return (t.exchange || '').toUpperCase() === up;
}

// Watch List's backend supports a true "omit limit for all" mode.
async function fetchAllTickers(): Promise<Ticker[]> {
  const res = await apiFetch('/api/tickers?offset=0');
  if (!res.ok) return [];
  const d = await res.json();
  return d.tickers || [];
}

// Scanner Flags' backend caps limit at 500 per call (no "unlimited" mode) —
// loop until the full total is accumulated.
async function fetchAllScanFlags(): Promise<ScanFlag[]> {
  let offset = 0;
  let all: ScanFlag[] = [];
  let total = Infinity;
  while (all.length < total) {
    const res = await apiFetch(`/api/admin/market-scan-summary?limit=500&offset=${offset}`);
    if (!res.ok) break;
    const d = await res.json();
    total = d.total ?? all.length;
    const batch: ScanFlag[] = d.flags || [];
    if (batch.length === 0) break;
    all = all.concat(batch);
    offset += batch.length;
  }
  return all;
}

// ─── Watchlist without scanner flags (Task 10) ───────────────────────────────

const WATCH_LIST_PAGE = 50;

function WatchlistTabNoFlags({ onViewStock }: { onViewStock: (ticker: string, exchange: string) => void }) {
  const [tickers, setTickers] = useState<Ticker[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalTickers, setTotalTickers] = useState(0);
  const [tickerOffset, setTickerOffset] = useState(0);
  const [exchangeFilter, setExchangeFilter] = useState<string>('ALL');
  const [tierFilter, setTierFilter] = useState<'ALL' | 'active' | 'watchlist' | 'monitoring'>('ALL');
  const [showManualOnly, setShowManualOnly] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTicker, setNewTicker] = useState({ ticker: '', exchange: 'ASX' });
  const [addResult, setAddResult] = useState<{ success: boolean; message: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [overrideModal, setOverrideModal] = useState<{ ticker: Ticker; action: 'promote' | 'demote' } | null>(null);
  const [visibleCount, setVisibleCount] = useState(20);
  const loadAll = useLoadAll<Ticker>(fetchAllTickers);

  const load = useCallback(async (newOffset = 0, append = false) => {
    if (newOffset === 0) setLoading(true); else setLoadingMore(true);
    try {
      const tierParam = tierFilter !== 'ALL' ? `&tier=${tierFilter}` : '';
      const exchParam = exchangeFilter !== 'ALL' ? `&exchange=${encodeURIComponent(exchangeFilter)}` : '';
      const res = await apiFetch(`/api/tickers?limit=${WATCH_LIST_PAGE}&offset=${newOffset}${tierParam}${exchParam}`);
      if (res.ok) {
        const d = await res.json();
        setTotalTickers(d.total || 0);
        if (append) setTickers(prev => [...prev, ...(d.tickers || [])]);
        else setTickers(d.tickers || []);
        setTickerOffset(newOffset + (d.tickers || []).length);
      }
    } finally { setLoading(false); setLoadingMore(false); }
  }, [tierFilter, exchangeFilter]);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => { setVisibleCount(20); }, [tierFilter, exchangeFilter, showManualOnly, loadAll.active]);

  const revealMore = () => {
    const needed = visibleCount + 50;
    if (!loadAll.active && needed > tickers.length && tickers.length < totalTickers) {
      load(tickerOffset, true);
    }
    setVisibleCount(needed);
  };

  const doPromote = async (t: Ticker, duration: number | null, reason: string) => {
    const until = duration ? new Date(Date.now() + duration * 86400000).toISOString() : null;
    const res = await apiFetch('/api/tickers/promote', {
      method: 'POST',
      body: JSON.stringify({ ticker: t.ticker, exchange: t.exchange, manual_override: true, manual_override_until: until, manual_override_reason: reason || null }),
    });
    if (res.ok) { setActionMsg(`${t.ticker} promoted to active`); load(0); }
    setOverrideModal(null);
  };

  const doDemote = async (t: Ticker, duration: number | null, reason: string) => {
    const until = duration ? new Date(Date.now() + duration * 86400000).toISOString() : null;
    const res = await apiFetch('/api/tickers/demote', {
      method: 'POST',
      body: JSON.stringify({ ticker: t.ticker, exchange: t.exchange, tier: 'watchlist', manual_override: true, manual_override_until: until, manual_override_reason: reason || null }),
    });
    if (res.ok) { setActionMsg(`${t.ticker} demoted to watchlist`); load(0); }
    setOverrideModal(null);
  };

  const addTicker = async (force = false) => {
    if (!newTicker.ticker.trim()) return;
    setAdding(true);
    setAddResult(null);
    try {
      const res = await apiFetch('/api/admin/ticker-management/add', {
        method: 'POST',
        body: JSON.stringify({ ticker: newTicker.ticker.trim().toUpperCase(), exchange: newTicker.exchange, force }),
      });
      const d = await res.json();
      if (!res.ok) {
        setAddResult({ success: false, message: d.detail || 'Error adding ticker' });
      } else if (d.success) {
        setActionMsg(`${d.ticker} added to watchlist tier`);
        setNewTicker({ ticker: '', exchange: 'ASX' });
        setAddResult(null);
        setShowAddForm(false);
        load(0);
      } else {
        setAddResult({ success: false, message: d.message || 'Could not validate ticker' });
      }
    } catch {
      setAddResult({ success: false, message: 'Request failed' });
    }
    setAdding(false);
  };

  const clearManualAction = async (t: Ticker) => {
    const res = await apiFetch(`/api/tickers/${t.id}/clear-manual`, { method: 'POST' });
    if (res.ok) { setActionMsg(`${t.ticker} returned to automatic management`); load(0); }
  };

  const doSetMonitoring = async (t: Ticker) => {
    const res = await apiFetch('/api/tickers/demote', {
      method: 'POST',
      body: JSON.stringify({ ticker: t.ticker, exchange: t.exchange, tier: 'monitoring', manual_override: true }),
    });
    if (res.ok) { setActionMsg(`${t.ticker} moved to monitoring`); load(0); }
  };

  // Load All holds the full unfiltered universe in memory — tier/exchange filters below
  // apply client-side against it so filter changes never re-fetch while active. In the
  // normal paginated view `tickers` is already server-filtered, so these checks are a
  // harmless no-op there.
  const sourceList = loadAll.active ? loadAll.items : tickers;
  const filtered = sourceList.filter(t => {
    if (showManualOnly && !t.manual_override) return false;
    if (tierFilter !== 'ALL' && t.tier !== tierFilter) return false;
    if (!tickerMatchesExchangeFilter(t, exchangeFilter)) return false;
    return true;
  });
  // Load All renders the full filtered set directly — native scrolling browses it,
  // no reveal/pagination control at all. Only the normal paginated view slices+reveals.
  const displayed = loadAll.active ? filtered : filtered.slice(0, visibleCount);
  const canRevealMore = !loadAll.active && (visibleCount < filtered.length || tickers.length < totalTickers);

  const counts = { monitoring: sourceList.filter(t => t.tier === 'monitoring').length, active: sourceList.filter(t => t.tier === 'active').length, watchlist: sourceList.filter(t => t.tier === 'watchlist').length };

  const getReasonDisplay = (t: Ticker): string => {
    if (t.manual_override) return 'Manual: ' + (t.manual_override_reason || 'Override');
    if (t.tier_reason) {
      let reason = t.tier_reason;
      try {
        const parsed = JSON.parse(t.tier_reason);
        if (parsed && typeof parsed === 'object' && parsed.reason) reason = String(parsed.reason);
      } catch { /* not JSON */ }
      return reason.slice(0, 40) + (reason.length > 40 ? '…' : '');
    }
    return '—';
  };

  return (
    <div className="space-y-4">
      {actionMsg && (
        <div className="bg-status-green-soft border border-border-green text-status-green-muted px-4 py-2 rounded text-sm flex justify-between">
          {actionMsg}<button onClick={() => setActionMsg(null)} className="ml-4 text-status-green">✕</button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        {[{ label: 'Monitoring', tier: 'monitoring' as const, cardCls: 'bg-accent-soft border-accent', textCls: 'text-text-bright', count: counts.monitoring, desc: 'Real-time price tracking' },
          { label: 'Active', tier: 'active' as const, cardCls: undefined, textCls: 'text-status-green', count: counts.active, desc: 'Signals run every cycle' },
          { label: 'Watchlist', tier: 'watchlist' as const, cardCls: undefined, textCls: 'text-status-amber', count: counts.watchlist, desc: 'Monitored, not traded' }
        ].map(({ label, tier, cardCls, textCls, count, desc }) => {
          const selectedBorder = tier === 'active' ? 'border-border-green' : tier === 'watchlist' ? 'border-border-amber' : 'border-border-base';
          const unselected = `bg-surface ${tierFilter === tier ? selectedBorder : 'border-border-base hover:border-border-light'}`;
          return (
            <button key={tier} onClick={() => setTierFilter(tierFilter === tier ? 'ALL' : tier)}
              className={`border rounded-lg p-3 text-left transition-colors ${cardCls || unselected}`}>
              <div className={`text-2xl font-bold ${textCls}`}>{count}</div>
              <div className="text-sm font-medium">{label}</div>
              <div className="text-xs text-text-secondary">{desc}</div>
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 items-center flex-wrap justify-between">
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1 flex-wrap">
            {(['ALL', 'ASX', 'NASDAQ', 'NYSE', 'AMEX', 'NYSE Arca'] as const).map(ex => (
              <button key={ex} onClick={() => setExchangeFilter(ex)} className={`text-xs px-3 py-1 rounded ${exchangeFilter === ex ? 'bg-status-blue-btn text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-surface-hover'}`}>{ex}</button>
            ))}
            <button onClick={() => setShowManualOnly(p => !p)} className={`text-xs px-3 py-1 rounded ${showManualOnly ? 'bg-accent text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-surface-hover'}`}>Manual</button>
          </div>
          <div className="flex gap-1">
            {(['ALL', 'monitoring', 'active', 'watchlist'] as const).map(t => (
              <button key={t} onClick={() => setTierFilter(t)} className={`text-xs px-3 py-1 rounded capitalize ${
                tierFilter === t
                  ? t === 'monitoring' ? 'bg-accent text-text-bright'
                  : t === 'active' ? 'bg-status-green-btn text-text-bright'
                  : t === 'watchlist' ? 'bg-status-amber-btn text-bg-deep'
                  : 'bg-border-light text-text-bright'
                : 'bg-surface-2 text-text-primary hover:bg-surface-hover'
              }`}>{t}</button>
            ))}
          </div>
          <span className="text-xs text-text-secondary self-center">{totalTickers} tickers</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAddForm(!showAddForm)} className="bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright text-xs px-3 py-1.5 rounded">+ Add Ticker</button>
          <button onClick={() => load(0)} className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-3 py-1.5 rounded">Refresh</button>
          {!loadAll.active && (
            <button onClick={() => loadAll.trigger()} disabled={loadAll.loading} className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright text-xs px-3 py-1.5 rounded">
              {loadAll.loading ? 'Loading all...' : `Load All (${totalTickers})`}
            </button>
          )}
        </div>
      </div>
      {loadAll.active && (
        <LoadAllBanner loadedAt={loadAll.loadedAt} loading={loadAll.loading} onRefresh={() => loadAll.refresh()} onExit={() => loadAll.reset()} />
      )}
      {showAddForm && (
        <div className="bg-surface border border-border-base rounded-lg p-4">
          <div className="flex gap-3 flex-wrap items-center">
            <input type="text" value={newTicker.ticker} onChange={e => setNewTicker({ ...newTicker, ticker: e.target.value.toUpperCase() })}
              placeholder="Ticker (e.g. CBA)" className="bg-surface-2 text-text-bright text-sm px-3 py-1.5 rounded border border-border-light w-36" />
            <select value={newTicker.exchange} onChange={e => setNewTicker({ ...newTicker, exchange: e.target.value })} className="bg-surface-2 text-text-bright text-sm px-3 py-1.5 rounded border border-border-light">
              <option value="ASX">ASX</option><option value="US">US</option>
            </select>
            <button onClick={() => addTicker(false)} disabled={adding || !newTicker.ticker.trim()} className="bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright text-sm px-4 py-1.5 rounded">
              {adding ? 'Checking...' : 'Validate & Add'}
            </button>
            <button onClick={() => { setShowAddForm(false); setAddResult(null); }} className="bg-surface-2 hover:bg-surface-hover text-text-bright text-sm px-4 py-1.5 rounded">Cancel</button>
          </div>
          <p className="text-xs text-text-muted mt-2">New tickers are validated by price lookup and added to the Watchlist tier.</p>
          {addResult && (
            <div className={`mt-2 text-sm ${addResult.success ? 'text-status-green' : 'text-status-red'}`}>
              {addResult.message}
              {!addResult.success && addResult.message.includes('force') && (
                <button onClick={() => addTicker(true)} className="ml-3 underline text-status-amber-muted text-xs">Add Anyway</button>
              )}
            </div>
          )}
        </div>
      )}
      {loading ? <div className="text-center text-text-secondary py-10">Loading...</div> : (
        <div className="bg-surface border border-border-base rounded-lg overflow-x-auto">
          <ScrollWindow
            showRevealButton={canRevealMore}
            revealLabel="Load next 50"
            onReveal={revealMore}
            revealing={!loadAll.active && loadingMore}
          >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-text-secondary border-b border-border-base sticky top-0 bg-surface z-10">
                <th className="text-left px-3 py-2">Ticker</th>
                <th className="text-left px-3 py-2">Exchange</th>
                <th className="text-left px-3 py-2">Tier</th>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-right px-3 py-2">Mkt Cap</th>
                <th className="text-right px-3 py-2 cursor-help" title="Full signal engine score (Rules + ML blend), 0–10. Different scale from Scanner Score on the Scanner Flags tab.">Signal Score</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-center px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(t => (
                <tr key={t.id} className="border-b border-border-base/50 hover:bg-surface-hover">
                  <td className="px-3 py-2">
                    <button onClick={() => onViewStock(t.ticker, t.exchange)} className="font-mono font-semibold text-text-bright hover:text-status-green transition-colors underline underline-offset-2">{t.ticker}</button>
                    {t.manual_override && <span className="ml-1.5 text-xs bg-accent-soft text-text-secondary px-1.5 py-0.5 rounded" title="Manually assigned">Manual</span>}
                  </td>
                  <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${t.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'}`}>{t.specific_exchange || t.exchange}</span></td>
                  <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 rounded capitalize ${t.tier === 'monitoring' ? 'bg-accent text-text-bright' : t.tier === 'active' ? 'bg-status-green-btn-hover text-text-bright' : t.tier === 'watchlist' ? 'bg-status-amber-btn text-bg-deep' : 'bg-surface-2 text-text-bright'}`}>{t.tier}</span></td>
                  <td className="px-3 py-2 text-text-primary text-xs max-w-[140px] truncate">{t.company_name || t.ticker}</td>
                  <td className="px-3 py-2 text-right text-text-primary text-xs">{fmtMarketCap(t.market_cap)}</td>
                  <td className="px-3 py-2 text-right">
                    {t.signal_score != null ? <span className={`font-mono text-xs ${t.signal_score >= 6.5 ? 'text-status-green' : t.signal_score <= 3.5 ? 'text-status-red' : 'text-status-amber'}`}>{t.signal_score.toFixed(2)}</span> : '—'}
                  </td>
                  <td className="px-3 py-2 text-text-muted text-xs max-w-[200px] truncate" title={getReasonDisplay(t)}>{getReasonDisplay(t)}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex gap-1 justify-center">
                      {t.tier === 'monitoring' && <button onClick={() => setOverrideModal({ ticker: t, action: 'promote' })} className="text-xs bg-status-green-btn-hover hover:bg-status-green-btn text-text-bright px-2 py-0.5 rounded" title="Return to Active">→A</button>}
                      {(t.tier === 'watchlist' || t.tier === 'active') && <button onClick={() => doSetMonitoring(t)} className="text-xs bg-accent hover:bg-accent-hover text-text-bright px-2 py-0.5 rounded" title="Move to Monitoring">M</button>}
                      {t.tier === 'watchlist' && <button onClick={() => setOverrideModal({ ticker: t, action: 'promote' })} className="text-xs bg-status-green-btn-hover hover:bg-status-green-btn text-text-bright px-2 py-0.5 rounded" title="Promote to Active">↑</button>}
                      {t.tier === 'active' && <button onClick={() => setOverrideModal({ ticker: t, action: 'demote' })} className="text-xs bg-status-amber-btn-hover hover:bg-status-amber-btn text-text-bright px-2 py-0.5 rounded" title="Demote to Watchlist">↓</button>}
                      {t.manual_override && <button onClick={() => clearManualAction(t)} className="text-xs bg-accent hover:bg-accent-hover text-text-bright px-2 py-0.5 rounded" title="Clear manual">✕</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </ScrollWindow>
          {filtered.length === 0 && <div className="text-center text-text-secondary py-8">No tickers match the current filter.</div>}
        </div>
      )}
      <div className="text-xs text-text-muted text-center">
        {loadAll.active ? `${filtered.length} of ${loadAll.items.length} loaded (Load All)` : `${Math.min(displayed.length, filtered.length)} of ${totalTickers} shown`}
      </div>
      {overrideModal && (
        <ManualOverrideModal
          ticker={overrideModal.ticker}
          action={overrideModal.action}
          onConfirm={(duration, reason) => {
            if (overrideModal.action === 'promote') doPromote(overrideModal.ticker, duration, reason);
            else doDemote(overrideModal.ticker, duration, reason);
          }}
          onClose={() => setOverrideModal(null)}
        />
      )}
    </div>
  );
}

// ─── Scanner Flags Tab ────────────────────────────────────────────────────────

const SCANNER_PAGE = 50;

function ScannerFlagsTab({ onViewStock }: { onViewStock: (ticker: string, exchange: string) => void }) {
  const [flags, setFlags] = useState<ScanFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalFlags, setTotalFlags] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState<string>('ALL');
  const [visibleCount, setVisibleCount] = useState(20);
  const loadAll = useLoadAll<ScanFlag>(fetchAllScanFlags);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/admin/market-scan-summary?limit=${SCANNER_PAGE}&offset=0`);
      if (res.ok) {
        const d = await res.json();
        setTotalFlags(d.total || 0);
        setFlags(d.flags || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setVisibleCount(20); }, [searchQuery, exchangeFilter, loadAll.active]);

  const sourceList = loadAll.active ? loadAll.items : flags;
  const filteredFlags = sourceList.filter(f => {
    if (exchangeFilter !== 'ALL' && (f.specific_exchange || f.exchange) !== exchangeFilter) return false;
    if (searchQuery.trim() && !f.ticker.toUpperCase().includes(searchQuery.trim().toUpperCase())) return false;
    return true;
  });
  // Load All renders the full filtered set directly — native scrolling browses it,
  // no reveal control at all (matches Watch List; Scanner Flags never had a reveal
  // control in the normal paginated view either, per Session 5b Phase 3).
  const displayedFlags = loadAll.active ? filteredFlags : filteredFlags.slice(0, visibleCount);
  const isFiltering = searchQuery.trim() !== '' || exchangeFilter !== 'ALL';

  if (loading) return <div className="text-center text-text-secondary py-10">Loading...</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Recent Scanner Flags (last 24h) — {totalFlags} tickers</h2>
        <div className="flex gap-2">
          <button onClick={() => load()} className="text-xs bg-surface-2 hover:bg-surface-hover text-text-bright px-3 py-1 rounded">Refresh</button>
          {!loadAll.active && (
            <button onClick={() => loadAll.trigger()} disabled={loadAll.loading} className="text-xs bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright px-3 py-1 rounded">
              {loadAll.loading ? 'Loading all...' : `Load All (${totalFlags})`}
            </button>
          )}
        </div>
      </div>
      {loadAll.active && (
        <LoadAllBanner loadedAt={loadAll.loadedAt} loading={loadAll.loading} onRefresh={() => loadAll.refresh()} onExit={() => loadAll.reset()} />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Filter by ticker..."
          className="bg-surface-2 text-text-bright text-xs px-3 py-1.5 rounded border border-border-light w-40"
        />
        <div className="flex gap-1 flex-wrap">
          {(['ALL', 'ASX', 'NASDAQ', 'NYSE', 'AMEX', 'NYSE Arca'] as const).map(ex => (
            <button key={ex} onClick={() => setExchangeFilter(ex)} className={`text-xs px-3 py-1 rounded ${exchangeFilter === ex ? 'bg-status-blue-btn text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-surface-hover'}`}>{ex}</button>
          ))}
        </div>
        {isFiltering && (
          <button onClick={() => { setSearchQuery(''); setExchangeFilter('ALL'); }} className="text-xs text-text-muted hover:text-text-primary underline">Clear filters</button>
        )}
        {isFiltering && <span className="text-xs text-text-muted">{filteredFlags.length} of {sourceList.length} loaded match</span>}
      </div>
      {sourceList.length === 0 ? (
        <div className="text-center text-text-secondary py-10">No scanner flags in the last 24 hours.</div>
      ) : filteredFlags.length === 0 ? (
        <div className="text-center text-text-secondary py-10">No loaded flags match the current filters.</div>
      ) : (
        <div className="bg-surface border border-border-base rounded-lg overflow-x-auto">
          <ScrollWindow showRevealButton={false}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-text-secondary border-b border-border-base sticky top-0 bg-surface z-10">
                <th className="text-left px-3 py-2">Ticker</th>
                <th className="text-left px-3 py-2">Exchange</th>
                <th className="text-right px-3 py-2">Flags</th>
                <th className="text-right px-3 py-2 cursor-help" title="Rule-based technical scanner score, 0–10. Different scale from Signal Score on the Watch List tab.">Scanner Score</th>
                <th className="text-right px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {displayedFlags.map((f, i) => (
                <tr key={i} className="border-b border-border-base/50 hover:bg-surface-hover/30">
                  <td className="px-3 py-2">
                    <button onClick={() => onViewStock(f.ticker, f.exchange)} className="font-mono text-status-amber-muted hover:text-status-amber-muted underline underline-offset-2">{f.ticker}</button>
                  </td>
                  <td className="px-3 py-2 text-text-secondary text-xs">{f.specific_exchange || f.exchange}</td>
                  <td className="px-3 py-2 text-right text-text-primary">{f.flag_count}</td>
                  <td className={`px-3 py-2 text-right font-mono text-xs ${f.latest_score >= 8 ? 'text-status-green' : 'text-text-bright'}`}>{f.latest_score?.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-text-secondary text-xs">
                    {f.latest_scanned_at ? new Date(f.latest_scanned_at).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-3 py-2 text-text-secondary text-xs truncate max-w-xs">
                    {(() => {
                      const parsed = parseFlags(f.latest_reason || '').map(flagToPlainEnglish).filter(Boolean) as { text: string; icon: string }[];
                      if (parsed.length > 0) return parsed.map(p => `${p.icon} ${p.text}`).join(', ');
                      return f.latest_reason;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </ScrollWindow>
        </div>
      )}
      <div className="text-xs text-text-muted text-center">
        {loadAll.active ? `${filteredFlags.length} of ${loadAll.items.length} loaded (Load All)` : `${Math.min(displayedFlags.length, filteredFlags.length)} of ${totalFlags} shown`}
      </div>
    </div>
  );
}

type ResearchTab = 'watch-this' | 'watchlist' | 'scanner-flags' | 'search';

export default function Research() {
  const location = useLocation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('ALL');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);
  const [selectedStock, setSelectedStock] = useState<StockDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState<ResearchTab>('watch-this');

  useEffect(() => {
    apiGet('/explorer/recent-searches')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setRecentSearches(d.searches || []); })
      .catch(() => {});
  }, []);

  // Handle direct navigation via URL params — switch to Search tab and load detail
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticker = params.get('ticker');
    const exch = params.get('exchange');
    if (ticker && exch) {
      setActiveTab('search');
      loadDetail(ticker, exch);
    }
  }, [location.search]);

  const doSearch = useCallback(async (q: string, exch: string) => {
    if (q.trim().length < 1) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams({ q, exchange: exch });
      const res = await apiGet(`/explorer/search?${params}`);
      if (res.ok) { const d = await res.json(); setSearchResults(d.results || []); }
    } catch {} finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query, exchangeFilter), 300);
    return () => clearTimeout(t);
  }, [query, exchangeFilter, doSearch]);

  const loadDetail = async (ticker: string, exch: string) => {
    setLoadingDetail(true);
    setSelectedStock(null);
    setSearchResults([]);
    setQuery('');
    try {
      const res = await apiGet(`/explorer/stock/${ticker}?exchange=${exch}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedStock(data);
        navigate(`/research?ticker=${ticker}&exchange=${exch}`, { replace: true });
        apiGet('/explorer/recent-searches').then(r => r.ok ? r.json() : null).then(d => { if (d) setRecentSearches(d.searches || []); }).catch(() => {});
        if (!data.fundamentals || Object.keys(data.fundamentals).length === 0) {
          apiPost(`/explorer/fetch-fundamentals/${ticker}?exchange=${exch}`, {})
            .then(r => r.ok ? r.json() : null)
            .then(fd => { if (fd?.fundamentals && Object.keys(fd.fundamentals).length > 0) setSelectedStock(prev => prev ? { ...prev, fundamentals: fd.fundamentals } : prev); })
            .catch(() => {});
        }
      }
    } catch {} finally { setLoadingDetail(false); }
  };

  // When a stock link is clicked from anywhere — navigate to Search tab then load
  const handleViewStock = (ticker: string, exchange: string) => {
    setActiveTab('search');
    loadDetail(ticker, exchange);
  };

  const closeStock = () => {
    setSelectedStock(null);
    navigate('/research', { replace: true });
  };

  const TABS: { id: ResearchTab; label: string }[] = [
    { id: 'watch-this',     label: 'Watch This' },
    { id: 'watchlist',      label: 'Watch List' },
    { id: 'scanner-flags',  label: 'Scanner Flags' },
    { id: 'search',         label: 'Search' },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-text-bright">Research</h1>

      {/* Tab navigation */}
      <div className="flex border-b border-border-base space-x-1 pb-px">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === t.id ? 'border-accent text-text-bright' : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Watch This */}
      {activeTab === 'watch-this' && (
        <WatchThisTab onViewStock={handleViewStock} />
      )}

      {/* Tab: Watchlist (without Scanner Flags) */}
      {activeTab === 'watchlist' && (
        <WatchlistTabNoFlags onViewStock={handleViewStock} />
      )}

      {/* Tab: Scanner Flags */}
      {activeTab === 'scanner-flags' && (
        <ScannerFlagsTab onViewStock={handleViewStock} />
      )}

      {/* Tab: Search */}
      {activeTab === 'search' && (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="flex space-x-2 relative">
            <div className="relative flex-1">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search stocks by ticker or company name"
                className="w-full bg-surface border border-border-light rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-border-green"
              />
              {searching && <span className="absolute right-3 top-3 text-text-secondary text-xs">Searching...</span>}
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-surface border border-border-base rounded-lg shadow-2xl max-h-80 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-border-base">
                    <p className="text-xs text-text-secondary">{searchResults.length} results</p>
                  </div>
                  {searchResults.map(r => (
                    <button key={`${r.exchange}-${r.ticker}`} onClick={() => loadDetail(r.ticker, r.exchange)}
                      className="w-full text-left px-4 py-3 hover:bg-surface-hover transition-colors border-b border-border-base last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-sm">{r.ticker}</span>
                          <span className="text-text-secondary ml-3 text-sm">{r.company_name}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'}`}>{r.exchange}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <select value={exchangeFilter} onChange={e => setExchangeFilter(e.target.value)}
              className="bg-surface border border-border-light rounded-lg px-3 py-3 text-sm"
            >
              <option value="ALL">All Markets</option>
              <option value="ASX">ASX</option>
              <option value="US">NYSE/NASDAQ</option>
            </select>
          </div>

          {/* Recent searches */}
          {!query && !selectedStock && !loadingDetail && recentSearches.length > 0 && (
            <div>
              <p className="text-xs text-text-muted mb-2 uppercase tracking-wide">Recent</p>
              <div className="grid grid-cols-5 gap-2">
                {recentSearches.slice(0, 10).map(r => (
                  <button key={`${r.exchange}-${r.ticker}`} onClick={() => loadDetail(r.ticker, r.exchange)}
                    className="flex items-center gap-1.5 bg-surface hover:bg-surface-hover border border-border-base rounded-lg px-2.5 py-2 text-sm transition-colors min-w-0"
                  >
                    <span className="font-bold truncate">{r.ticker}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${r.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'}`}>{r.exchange}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {loadingDetail && <div className="text-text-secondary text-sm py-4 text-center">Loading stock data...</div>}

          {selectedStock && !loadingDetail && (
            <StockDetailPanel stock={selectedStock} onClose={closeStock} />
          )}
        </div>
      )}
    </div>
  );
}
